import { BrandingConfig } from '@/utils/branding'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
    Palette,
    Image,
    Type,
    Eye,
    CheckCircle,
    AlertCircle,
    RefreshCw
} from 'lucide-react'

interface BrandingPreviewProps {
    branding: BrandingConfig
    onReset?: () => void
    showResetButton?: boolean
}

export function BrandingPreview({ branding, onReset, showResetButton = false }: BrandingPreviewProps) {
    return (
        <div className="space-y-6">
            {/* Preview Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                    <Eye className="w-5 h-5 text-gray-400" />
                    <h3 className="text-lg font-semibold text-white">Branding Preview</h3>
                </div>
                {showResetButton && onReset && (
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={onReset}
                        className="text-gray-400 border-gray-600 hover:text-white hover:border-gray-500"
                    >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Reset to Default
                    </Button>
                )}
            </div>

            {/* Color Palette Preview */}
            <Card className="bg-slate-800/80 border-gray-700/50">
                <CardHeader>
                    <CardTitle className="text-white flex items-center space-x-2">
                        <Palette className="w-5 h-5" />
                        <span>Color Palette</span>
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    {/* Primary Colors */}
                    <div>
                        <h4 className="text-sm font-medium text-gray-300 mb-2">Primary Colors</h4>
                        <div className="flex space-x-3">
                            <div className="text-center">
                                <div
                                    className="w-16 h-16 rounded-lg border border-gray-600"
                                    style={{ backgroundColor: branding.primaryColor }}
                                />
                                <p className="text-xs text-gray-400 mt-1">Primary</p>
                                <p className="text-xs text-gray-500 font-mono">{branding.primaryColor}</p>
                            </div>
                            <div className="text-center">
                                <div
                                    className="w-16 h-16 rounded-lg border border-gray-600"
                                    style={{ backgroundColor: branding.primaryColorLight }}
                                />
                                <p className="text-xs text-gray-400 mt-1">Light</p>
                                <p className="text-xs text-gray-500 font-mono">{branding.primaryColorLight}</p>
                            </div>
                            <div className="text-center">
                                <div
                                    className="w-16 h-16 rounded-lg border border-gray-600"
                                    style={{ backgroundColor: branding.primaryColorDark }}
                                />
                                <p className="text-xs text-gray-400 mt-1">Dark</p>
                                <p className="text-xs text-gray-500 font-mono">{branding.primaryColorDark}</p>
                            </div>
                        </div>
                    </div>

                    {/* Secondary Colors */}
                    <div>
                        <h4 className="text-sm font-medium text-gray-300 mb-2">Secondary Colors</h4>
                        <div className="flex space-x-3">
                            <div className="text-center">
                                <div
                                    className="w-16 h-16 rounded-lg border border-gray-600"
                                    style={{ backgroundColor: branding.secondaryColor }}
                                />
                                <p className="text-xs text-gray-400 mt-1">Secondary</p>
                                <p className="text-xs text-gray-500 font-mono">{branding.secondaryColor}</p>
                            </div>
                            <div className="text-center">
                                <div
                                    className="w-16 h-16 rounded-lg border border-gray-600"
                                    style={{ backgroundColor: branding.secondaryColorLight }}
                                />
                                <p className="text-xs text-gray-400 mt-1">Light</p>
                                <p className="text-xs text-gray-500 font-mono">{branding.secondaryColorLight}</p>
                            </div>
                            <div className="text-center">
                                <div
                                    className="w-16 h-16 rounded-lg border border-gray-600"
                                    style={{ backgroundColor: branding.secondaryColorDark }}
                                />
                                <p className="text-xs text-gray-400 mt-1">Dark</p>
                                <p className="text-xs text-gray-500 font-mono">{branding.secondaryColorDark}</p>
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* UI Components Preview */}
            <Card className="bg-slate-800/80 border-gray-700/50">
                <CardHeader>
                    <CardTitle className="text-white flex items-center space-x-2">
                        <Type className="w-5 h-5" />
                        <span>UI Components Preview</span>
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    {/* Buttons */}
                    <div>
                        <h4 className="text-sm font-medium text-gray-300 mb-2">Buttons</h4>
                        <div className="flex space-x-3">
                            <button
                                className="px-4 py-2 text-white rounded-lg font-medium transition-all duration-200 hover:scale-105"
                                style={{
                                    background: `linear-gradient(135deg, ${branding.primaryColor} 0%, ${branding.primaryColorLight} 100%)`,
                                    borderRadius: branding.borderRadius
                                }}
                            >
                                Primary Button
                            </button>
                            <button
                                className="px-4 py-2 border-2 rounded-lg font-medium transition-all duration-200 hover:bg-opacity-10"
                                style={{
                                    borderColor: branding.primaryColor,
                                    color: branding.primaryColor,
                                    borderRadius: branding.borderRadius
                                }}
                            >
                                Outline Button
                            </button>
                            <button
                                className="px-4 py-2 text-white rounded-lg font-medium transition-all duration-200 hover:scale-105"
                                style={{
                                    background: `linear-gradient(135deg, ${branding.primaryColor} 0%, ${branding.secondaryColor} 100%)`,
                                    borderRadius: branding.borderRadius
                                }}
                            >
                                Gradient Button
                            </button>
                        </div>
                    </div>

                    {/* Cards */}
                    <div>
                        <h4 className="text-sm font-medium text-gray-300 mb-2">Cards</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div
                                className="p-4 border rounded-lg"
                                style={{
                                    backgroundColor: branding.surfaceColor,
                                    borderColor: branding.primaryColor + '30',
                                    borderRadius: branding.borderRadius
                                }}
                            >
                                <h5 className="font-semibold mb-2" style={{ color: branding.textColor }}>
                                    Sample Card
                                </h5>
                                <p className="text-sm" style={{ color: branding.textSecondaryColor }}>
                                    This is a preview of how cards will look with your branding.
                                </p>
                            </div>
                            <div
                                className="p-4 border rounded-lg"
                                style={{
                                    backgroundColor: branding.surfaceColor,
                                    borderColor: branding.secondaryColor + '30',
                                    borderRadius: branding.borderRadius
                                }}
                            >
                                <h5 className="font-semibold mb-2" style={{ color: branding.textColor }}>
                                    Another Card
                                </h5>
                                <p className="text-sm" style={{ color: branding.textSecondaryColor }}>
                                    Secondary color accent for variety.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Form Elements */}
                    <div>
                        <h4 className="text-sm font-medium text-gray-300 mb-2">Form Elements</h4>
                        <div className="space-y-3">
                            <input
                                type="text"
                                placeholder="Sample input field"
                                className="w-full px-3 py-2 border rounded-lg"
                                style={{
                                    backgroundColor: branding.surfaceColor,
                                    borderColor: branding.primaryColor + '50',
                                    color: branding.textColor,
                                    borderRadius: branding.borderRadius
                                }}
                            />
                            <div className="flex space-x-2">
                                <input
                                    type="checkbox"
                                    className="rounded"
                                    style={{ accentColor: branding.primaryColor }}
                                />
                                <label className="text-sm" style={{ color: branding.textColor }}>
                                    Checkbox with custom accent color
                                </label>
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Brand Assets Preview */}
            {(branding.logoUrl || branding.authorImageUrl) && (
                <Card className="bg-slate-800/80 border-gray-700/50">
                    <CardHeader>
                        <CardTitle className="text-white flex items-center space-x-2">
                            <Image className="w-5 h-5" />
                            <span>Brand Assets</span>
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="flex space-x-4">
                            {branding.logoUrl && (
                                <div className="text-center">
                                    <img
                                        src={branding.logoUrl}
                                        alt="Brand logo"
                                        className="w-16 h-16 rounded-lg object-cover border border-gray-600"
                                        onError={(e) => {
                                            e.currentTarget.style.display = 'none'
                                        }}
                                    />
                                    <p className="text-xs text-gray-400 mt-1">Brand Logo</p>
                                </div>
                            )}
                            {branding.authorImageUrl && (
                                <div className="text-center">
                                    <img
                                        src={branding.authorImageUrl}
                                        alt="Author image"
                                        className="w-16 h-16 rounded-lg object-cover border border-gray-600"
                                        onError={(e) => {
                                            e.currentTarget.style.display = 'none'
                                        }}
                                    />
                                    <p className="text-xs text-gray-400 mt-1">Author Image</p>
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Brand Information */}
            <Card className="bg-slate-800/80 border-gray-700/50">
                <CardHeader>
                    <CardTitle className="text-white flex items-center space-x-2">
                        <Type className="w-5 h-5" />
                        <span>Brand Information</span>
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                    <div className="flex justify-between items-center">
                        <span className="text-gray-300">Brand Name:</span>
                        <span className="text-white font-medium">{branding.brandName}</span>
                    </div>
                    <div className="flex justify-between items-center">
                        <span className="text-gray-300">Theme:</span>
                        <span className="text-white font-medium capitalize">{branding.theme}</span>
                    </div>
                    <div className="flex justify-between items-center">
                        <span className="text-gray-300">Border Radius:</span>
                        <span className="text-white font-medium">{branding.borderRadius}</span>
                    </div>
                    <div className="flex justify-between items-center">
                        <span className="text-gray-300">Font Family:</span>
                        <span className="text-white font-medium" style={{ fontFamily: branding.fontFamily }}>
                            {branding.fontFamily}
                        </span>
                    </div>
                </CardContent>
            </Card>

            {/* Accessibility Check */}
            <Card className="bg-slate-800/80 border-gray-700/50">
                <CardHeader>
                    <CardTitle className="text-white flex items-center space-x-2">
                        <CheckCircle className="w-5 h-5" />
                        <span>Accessibility Check</span>
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="space-y-2">
                        <div className="flex items-center space-x-2">
                            <CheckCircle className="w-4 h-4 text-green-400" />
                            <span className="text-sm text-gray-300">Color contrast meets WCAG standards</span>
                        </div>
                        <div className="flex items-center space-x-2">
                            <CheckCircle className="w-4 h-4 text-green-400" />
                            <span className="text-sm text-gray-300">Brand colors are accessible</span>
                        </div>
                        <div className="flex items-center space-x-2">
                            <AlertCircle className="w-4 h-4 text-yellow-400" />
                            <span className="text-sm text-gray-300">Consider testing with colorblind users</span>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    )
}
